import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "../ThemeContext";
import ChatbotTitleIcon from "../../../assets/images/chatbottitleicon.svg";
import chatbotIcon from "../../../assets/images/botIcon.png";

const CHAT_WIDTH_KEY = "inc-chat-panel-width";
const DEFAULT_CHAT_WIDTH = 550;
const MIN_CHAT_WIDTH = 380;

const clampChatWidth = (value) => {
  const max = Math.min(720, Math.max(MIN_CHAT_WIDTH, window.innerWidth - 320));
  return Math.min(max, Math.max(MIN_CHAT_WIDTH, value));
};

const readStoredWidth = () => {
  const stored = Number(localStorage.getItem(CHAT_WIDTH_KEY));
  if (!Number.isFinite(stored) || stored === 480) return DEFAULT_CHAT_WIDTH;
  return clampChatWidth(stored);
};

const Chat = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_CHAT_WIDTH);
  const dragRef = useRef({ startX: 0, startWidth: DEFAULT_CHAT_WIDTH });
  const isDarkMode = theme === "dark";
  const botBackgroundColor = isDarkMode ? "#0B1220" : "#f8fafc";
  const botMessageColor = isDarkMode ? "#1E293B" : "#e8f4fb";
  const botTextColor = isDarkMode ? "#E2E8F0" : "#0f172a";
  const userMessageColor = isDarkMode ? "#0369A1" : "#0f172a";
  const sentIcon = isDarkMode ? "#7DD3FC" : "#0284c7";
  const placeHolderText = isDarkMode ? "#94A3B8" : "#64748b";
  const inputBackgroundColor = isDarkMode ? "#111827" : "#ffffff";
  const borderColor = isDarkMode ? "#1E293B" : "#e2e8f0";
  const chipTextColor = isDarkMode ? "#E2E8F0" : "#334155";
  const chipBackground = isDarkMode ? "#1E293B" : "#ffffff";
  const chipHoverBackground = isDarkMode ? "#334155" : "rgba(15, 23, 42, 0.06)";
  const cardBackground = isDarkMode ? "#111827" : "#ffffff";
  const linkColor = isDarkMode ? "#7DD3FC" : "#0369A1";
  const feedbackIconColor = isDarkMode ? "#94A3B8" : "#444746";

  useEffect(() => {
    setPanelWidth(readStoredWidth());
  }, []);

  useLayoutEffect(() => {
    const isMobile = window.innerWidth <= 768;
    document.documentElement.classList.toggle("chat-drawer-open", isOpen);
    document.documentElement.classList.toggle("chat-resizing", isResizing);
    document.documentElement.style.setProperty(
      "--inc-chat-panel",
      isOpen && !isMobile ? `${panelWidth}px` : "0px"
    );
    window.dispatchEvent(new Event("resize"));
  }, [isOpen, isResizing, panelWidth]);

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove("chat-drawer-open", "chat-resizing");
      document.documentElement.style.setProperty("--inc-chat-panel", "0px");
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const walkShadows = (root, visit) => {
      if (!root) return;
      visit(root);
      root.querySelectorAll("*").forEach((node) => {
        if (node.shadowRoot) walkShadows(node.shadowRoot, visit);
      });
    };

    const applyMessengerTheme = () => {
      document.querySelectorAll("df-messenger, df-messenger-chat").forEach((el) => {
        walkShadows(el.shadowRoot, (root) => {
          root
            .querySelectorAll("header, [part='titlebar'], [class*='titlebar'], [class*='title-bar']")
            .forEach((node) => {
              node.style.display = "none";
            });

          let style = root.getElementById("inc-df-theme");
          if (!style) {
            style = document.createElement("style");
            style.id = "inc-df-theme";
            root.appendChild(style);
          }
          style.textContent = `
            :host {
              color-scheme: ${isDarkMode ? "dark" : "light"};
              --df-messenger-chat-border: none;
              --df-messenger-titlebar-border: none;
              --df-messenger-titlebar-border-bottom: none;
              --df-messenger-chat-window-box-shadow: none;
              --df-messenger-border-color: ${borderColor};
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
              background: ${botBackgroundColor} !important;
            }
            ${isDarkMode ? `
              textarea, input, [contenteditable="true"] {
                color: #E2E8F0 !important;
                caret-color: #7DD3FC;
                background: ${inputBackgroundColor} !important;
              }
              textarea::placeholder, input::placeholder {
                color: #94A3B8 !important;
              }
            ` : ""}
          `;
        });
      });
    };

    applyMessengerTheme();
    const timer = window.setInterval(applyMessengerTheme, 400);
    return () => window.clearInterval(timer);
  }, [isOpen, isDarkMode, borderColor, botBackgroundColor, inputBackgroundColor]);

  useEffect(() => {
    if (document.querySelector('script[src*="df-messenger"]')) return;
    if (customElements.get("df-messenger")) return;

    const script = document.createElement("script");
    script.src =
      "https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/df-messenger.js";
    script.async = true;
    script.onerror = (error) => {
      console.error("Error loading Dialogflow Messenger script:", error);
    };
    document.body.appendChild(script);
  }, []);

  const onResizeMove = useCallback((event) => {
    const nextWidth = clampChatWidth(
      dragRef.current.startWidth + (dragRef.current.startX - event.clientX)
    );
    setPanelWidth(nextWidth);
  }, []);

  const stopResize = useCallback(() => {
    setIsResizing(false);
    setPanelWidth((current) => {
      const next = clampChatWidth(current);
      localStorage.setItem(CHAT_WIDTH_KEY, String(next));
      return next;
    });
    window.removeEventListener("pointermove", onResizeMove);
    window.removeEventListener("pointerup", stopResize);
  }, [onResizeMove]);

  const startResize = (event) => {
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startWidth: panelWidth };
    setIsResizing(true);
    window.addEventListener("pointermove", onResizeMove);
    window.addEventListener("pointerup", stopResize);
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/themes/df-messenger-default.css"
      />

      {!isOpen && (
        <button
          type="button"
          className="inc-chat-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open INC assistant"
        >
          <img src={chatbotIcon} alt="" />
        </button>
      )}

      <aside
        className={`inc-chat-panel ${isOpen ? "is-open" : ""} ${isResizing ? "is-resizing" : ""}`}
        style={{ width: panelWidth }}
        aria-hidden={!isOpen}
      >
        <div
          className="inc-chat-resizer"
          onPointerDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize assistant"
        />
        <header className="inc-chat-panel-bar">
          <img src={ChatbotTitleIcon} alt="" />
          <div>
            <strong>INC Assistant</strong>
            <span>Network support</span>
          </div>
          <em className="inc-chat-online">Online</em>
          <button
            type="button"
            className="inc-chat-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close assistant"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="inc-chat-panel-body">
          <df-messenger
            project-id="vvdn-act"
            intent="WelcomeEvent"
            agent-id="d1c7156d-e5f8-4889-8f35-6844fcb36d38"
            language-code="en"
            max-query-length="-1"
            storage-option="sessionStorage"
          >
            <df-messenger-chat
              placeholder-text="Ask about devices, alerts, or troubleshooting..."
              bot-actor-image={chatbotIcon}
            ></df-messenger-chat>
          </df-messenger>
        </div>
      </aside>

      <style>
        {`
    .inc-chat-panel {
      --df-messenger-chat-border: none;
      --df-messenger-titlebar-border: none;
      --df-messenger-titlebar-border-bottom: none;
      --df-messenger-chat-window-box-shadow: none;
      --df-messenger-border-color: ${borderColor};
    }

    .inc-chat-panel df-messenger,
    .inc-chat-panel df-messenger-chat {
      position: relative !important;
      inset: auto !important;
      width: 100% !important;
      height: 100% !important;
      right: auto !important;
      bottom: auto !important;
      top: auto !important;
      left: auto !important;
      filter: none !important;
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
      --df-messenger-chat-window-offset: 0px;
      --df-messenger-chat-window-width: 100%;
      --df-messenger-chat-window-height: 100%;
      --df-messenger-chat-border-radius: 0px;
      --df-messenger-chat-padding: 18px 18px 8px;
      --df-messenger-font-family: Inter, "Segoe UI", system-ui, sans-serif;
      --df-messenger-font-size: 13.5px;
      --df-messenger-font-color: ${botTextColor};
      --df-messenger-element-font-color: ${chipTextColor};
      --df-messenger-border-color: ${borderColor};
      --df-messenger-primary-color: #0284c7;
      --df-messenger-chat-border: none;
      --df-messenger-chat-window-box-shadow: none;
      --df-messenger-titlebar-background: transparent;
      --df-messenger-titlebar-border: none;
      --df-messenger-titlebar-border-bottom: none;
      --df-messenger-chat-background: ${botBackgroundColor};
      --df-messenger-chat-bubble-background: ${botBackgroundColor};
      --df-messenger-message-user-background: ${userMessageColor};
      --df-messenger-message-bot-background: ${botMessageColor};
      --df-messenger-message-bot-font-color: ${botTextColor};
      --df-messenger-message-user-font-color: #ffffff;
      --df-messenger-message-bot-writing-font-color: ${botTextColor};
      --df-messenger-message-feedback-icon-font-color: ${feedbackIconColor};
      --df-messenger-message-feedback-icon-font-color-active: ${sentIcon};
      --df-messenger-input-background: ${inputBackgroundColor};
      --df-messenger-input-box-background: ${inputBackgroundColor};
      --df-messenger-input-font-color: ${botTextColor};
      --df-messenger-input-placeholder-color: ${placeHolderText};
      --df-messenger-input-placeholder-font-color: ${placeHolderText};
      --df-messenger-input-border-top: 1px solid ${borderColor};
      --df-messenger-input-box-border: 1px solid ${borderColor};
      --df-messenger-input-box-focus-border: 1px solid #0284c7;
      --df-messenger-send-icon: ${sentIcon};
      --df-messenger-send-icon-color: ${sentIcon};
      --df-messenger-chips-border-radius: 8px;
      --df-messenger-chips-border-color: ${borderColor};
      --df-messenger-chips-font-size: 12px;
      --df-messenger-chips-font-color: ${chipTextColor};
      --df-messenger-chips-font-weight: 600;
      --df-messenger-chips-background: ${chipBackground};
      --df-messenger-chips-background-hover: ${chipHoverBackground};
      --df-messenger-card-background: ${cardBackground};
      --df-messenger-card-border: 1px solid ${borderColor};
      --df-messenger-button-font-color: ${chipTextColor};
      --df-messenger-link-font-color: ${linkColor};
      --df-messenger-link-visited-font-color: ${linkColor};
      --df-messenger-link-hover-font-color: ${sentIcon};
    }

    .inc-chat-panel df-messenger::part(chat-window) {
      position: absolute !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      max-height: none !important;
      border: 0 !important;
      outline: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background: ${botBackgroundColor} !important;
    }

    .inc-chat-panel df-messenger::part(titlebar),
    .inc-chat-panel df-messenger-chat::part(titlebar) {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }

    .inc-chat-panel df-messenger::part(message-list) {
      background: ${botBackgroundColor};
    }

    .inc-chat-panel df-messenger::part(message-bot) {
      background: ${botMessageColor};
      border: 0;
      color: ${botTextColor};
    }

    .inc-chat-panel df-messenger::part(message-user) {
      background: ${userMessageColor};
      color: #ffffff !important;
    }

    .inc-chat-panel df-messenger::part(input-wrapper) {
      border-top: 1px solid ${borderColor};
      background: ${inputBackgroundColor};
    }

    .inc-chat-panel df-messenger::part(input-box) {
      border: 1px solid ${borderColor};
      background: ${inputBackgroundColor};
      color: ${botTextColor};
    }
  `}
      </style>
    </>
  );
};

export default Chat;
